import { useEffect, useState } from "react"
import { ArrowUp } from "lucide-react"

import { cn } from "../../lib/utils"

/** Show the button once the page is scrolled this far down. */
const SHOW_AFTER_PX = 400

/** Floating button that appears after the window scrolls past a threshold and
 *  smooth-scrolls back to the top. Self-contained: renders nothing until
 *  visible. Assumes the document scrolls (the default AppShell layout). */
export function ScrollToTop({ className }: { className?: string }) {
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > SHOW_AFTER_PX)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  if (!visible) {
    return null
  }

  return (
    <button
      type="button"
      aria-label="Scroll to top"
      onClick={() =>
        window.scrollTo({
          top: 0,
          behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
            ? "instant"
            : "smooth",
        })
      }
      className={cn(
        "fixed bottom-6 right-6 z-40 inline-flex size-10 items-center justify-center rounded-full",
        "border border-border bg-background text-foreground shadow-floating transition-colors",
        "hover:bg-muted/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
        className
      )}
    >
      <ArrowUp className="size-4" />
    </button>
  )
}
